import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Dropdown, Option } from '@fluentui/react-components';
import { InlineChart } from '@bingads-webui-campaign-react/ads-studio-shared-components';
import { AdsStudioSpinner } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { ImpressionsID, CTRID, ClicksID, OverallID, metricsMap } from '../../constants';
import { usePerformanceContentStyles } from '../../styles';
import { ItemExternalLink } from './item-external-link';

const defaultMetricValue = OverallID;
const defaultMetricLabel = _TL_('Overall metrics');

const metricOptions = [
  {
    label: defaultMetricLabel,
    value: defaultMetricValue,
  },
  {
    label: _TL_('CTR'),
    value: CTRID,
  },
  {
    label: _TL_('Clicks'),
    value: ClicksID,
  },
  {
    label: _TL_('Impressions'),
    value: ImpressionsID,
  },
];

export const PerformanceContent = (props) => {
  const {
    onGotoAds, i18n, loadPerfTimeSeriesData, imageId, folderAssetType, getGranularityEnum, perfMetricDateRange,
  } = props;
  const classes = usePerformanceContentStyles();
  const [isMetricLoading, setIsMetricLoading] = useState(false);
  const [metricData, setMetricData] = useState([]);
  const [granularity, setGranularity] = useState();

  const loadSeriesData = useCallback((selectedMetricValues) => {
    let metric;
    if (!granularity) {
      return;
    }
    if (selectedMetricValues === OverallID) {
      metric = metricsMap.map(({ name }) => name).join(',');
    } else {
      metric = metricsMap.find(({ id }) => id === selectedMetricValues).name;
    }
    const timeSeriesParams = {
      metric,
      granularity,
      isMovingAverageOn: false,
    };
    const filters = {
      searchText: imageId,
      hasPerfData: true,
      assetTypeFilter: folderAssetType,
    };
    setIsMetricLoading(true);
    loadPerfTimeSeriesData(timeSeriesParams, filters).then((result) => {
      setMetricData(result);
    }).finally(() => {
      setIsMetricLoading(false);
    });
  }, [folderAssetType, granularity, imageId, loadPerfTimeSeriesData]);

  const onOptionSelect = useCallback((ev, data) => {
    const { optionValue } = data || {};
    loadSeriesData(optionValue);
  }, [loadSeriesData]);

  useEffect(() => {
    loadSeriesData(defaultMetricValue);
  }, [loadSeriesData]);

  useEffect(() => {
    getGranularityEnum(perfMetricDateRange).then((granularityEnum) => {
      setGranularity(granularityEnum);
    });
  }, [getGranularityEnum, perfMetricDateRange]);

  return (
    <div className="performance-content">
      <Dropdown className={classNames('performance-dropdown', classes.dropdown)} aria-label={i18n.getString(_TL_('metric types'))} defaultValue={i18n.getString(defaultMetricLabel)} defaultSelectedOptions={[defaultMetricValue]} onOptionSelect={onOptionSelect}>
        {metricOptions.map(option => (
          <Option key={option.value} value={option.value}>
            {i18n.getString(option.label)}
          </Option>
        ))}
      </Dropdown>
      <div className={classNames('performance-metric', classes.metricBox)}>
        <div className={classNames(isMetricLoading && classes.loading)}>
          <InlineChart data={metricData} />
        </div>
        {isMetricLoading && <AdsStudioSpinner className={classes.spinner} />}
      </div>
      <ItemExternalLink onClick={onGotoAds} text={i18n.getString(_TL_('View all ads using this image'))} />
    </div>
  );
};

PerformanceContent.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  onGotoAds: PropTypes.func.isRequired,
  loadPerfTimeSeriesData: PropTypes.func.isRequired,
  imageId: PropTypes.string.isRequired,
  folderAssetType: PropTypes.string.isRequired,
};
